import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { EASE_OUT, SPRING_POP, SPRING_SOFT, SPRING_SETTLE, prefersReducedMotion } from '../../shared/motion.ts'
import { useCountUp } from './useCountUp'
import { GameHeader } from './components/GameHeader'
import { BottomSheetDrawer, type SheetTab } from './components/BottomSheetDrawer'
import { BetDock } from './components/BetDock'
import brandMark from '../../shared/assets/snowfold-atlas-mark.png'
import safeCrystal from '../../shared/assets/mines-safe.webp'
import hazardCore from '../../shared/assets/mines-hazard.webp'
import hiddenCellArt from '../../shared/assets/mines-hidden.webp'
import minesWorld from '../../shared/assets/mines-world.webp'
import {
  fetchBalance,
  getInitData,
  loginDev,
  loginWithInitData,
  minesCashout,
  minesOpen,
  minesStart,
  minesState,
  takeTokenFromUrl,
  type MinesState,
} from './api'
import { goLobby } from './lobby'

const QUICK_BETS = [50, 100, 250, 500]
const MINE_LEVELS = [1, 3, 5, 10]

export default function App() {
  const { t, i18n } = useTranslation()
  useEffect(() => {
    document.title = t('title')
  }, [t, i18n.language])
  const [token, setToken] = useState<string | null>(() => takeTokenFromUrl())
  const [balance, setBalance] = useState(0)
  const [authLoading, setAuthLoading] = useState(true)
  const [authError, setAuthError] = useState<string | null>(null)
  const [authRetry, setAuthRetry] = useState(0)
  const [bet, setBet] = useState(100)
  const [mines, setMines] = useState(3)
  const [round, setRound] = useState<MinesState | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [restoreStatus, setRestoreStatus] = useState<'loading' | 'ready' | 'error'>(
    token ? 'loading' : 'ready',
  )
  const [restoreError, setRestoreError] = useState<string | null>(null)
  const [balanceWarning, setBalanceWarning] = useState<string | null>(null)
  const [balanceRefreshing, setBalanceRefreshing] = useState(false)
  const [lastActionCell, setLastActionCell] = useState<number | null>(null)
  const [sheetOpen, setSheetOpen] = useState(false)
  const [showChips, setShowChips] = useState(false)
  const actionLockRef = useRef(false)
  const restoreLockRef = useRef(false)
  const balanceRefreshRef = useRef(false)

  useEffect(() => {
    let cancelled = false
    const restoreForAuth = async (tok: string) => {
      setRestoreStatus('loading')
      setRestoreError(null)
      let hasActiveRound = false
      try {
        const st = await minesState(tok)
        if (cancelled) return
        if (st.status === 'active') {
          setRound(st)
          hasActiveRound = true
        }
      } catch (e) {
        if (cancelled) return
        const raw = e instanceof Error ? e.message : ''
        if (!raw.toLowerCase().includes('no active round')) {
          setRestoreStatus('error')
          setRestoreError(t('restoreFailed'))
          return
        }
      }

      if (hasActiveRound) {
        setRestoreStatus('ready')
        return
      }

      // State and balance are one recovery decision. If no active round is
      // confirmed, refresh the wallet again so a round that settled between
      // the earlier balance read and state check cannot unlock stale funds.
      setRound(null)
      try {
        const { balance: bal } = await fetchBalance(tok)
        if (cancelled) return
        setBalance(bal)
        setBalanceWarning(null)
        setRestoreStatus('ready')
      } catch {
        if (cancelled) return
        setBalanceWarning(t('balanceRefreshFailed'))
        setRestoreStatus('error')
        setRestoreError(t('restoreBalanceFailed'))
      }
    }
    setAuthLoading(true)
    setAuthError(null)
    void (async () => {
      try {
        if (token) {
          const { balance: bal } = await fetchBalance(token)
          if (cancelled) return
          setBalance(bal)
          await restoreForAuth(token)
          if (!cancelled) setAuthLoading(false)
          return
        }
        const init = await getInitData()
        const res = init ? await loginWithInitData(init) : await loginDev()
        if (cancelled) return
        setToken(res.token)
        setBalance(res.user.balance)
        await restoreForAuth(res.token)
        if (!cancelled) setAuthLoading(false)
      } catch (e) {
        if (!cancelled) {
          void e
          setAuthError(t('authFailed'))
          setAuthLoading(false)
        }
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authRetry])

  const demoBridgeRef = useRef<{ ready: boolean; stop?: () => void } | null>(null)
  useEffect(() => {
    if (import.meta.env.VITE_DEMO_MODE === 'true') {
      return () => {
        demoBridgeRef.current?.stop?.()
        demoBridgeRef.current = null
      }
    }
  }, [])
  useEffect(() => {
    if (import.meta.env.VITE_DEMO_MODE === 'true') {
      const initializationError = authError ? 'auth_failed' : (restoreStatus === 'error' ? 'initialization_failed' : undefined)
      const usable = Boolean(token) && !authLoading && !authError && (restoreStatus === 'ready')
      const previous = demoBridgeRef.current
      // Latch the first usable UI: ordinary quote/table/round updates must not drop host messages.
      if (previous?.ready || (!usable && previous)) return
      if (!initializationError && !usable) return
      previous?.stop?.()
      const pending: { ready: boolean; stop?: () => void } = { ready: usable }
      demoBridgeRef.current = pending
      void import('../../shared/demo/iframeBridge.ts').then(({ startDemoIframeBridge }) => {
        if (demoBridgeRef.current !== pending) return
        pending.stop = startDemoIframeBridge({
          game: 'mines',
          locale: i18n.language,
          getLocale: () => i18n.language,
          setLocale: (locale) => i18n.changeLanguage(locale),
          error: initializationError,
        })
      }).catch(() => {
        if (demoBridgeRef.current === pending) demoBridgeRef.current = null
        // A bridge chunk failure leaves the host's bounded timeout in charge.
      })
    }
  }, [authLoading, authError, token, i18n, restoreStatus])

  const refreshBal = async (tok: string) => {
    if (balanceRefreshRef.current) return false
    balanceRefreshRef.current = true
    setBalanceRefreshing(true)
    try {
      const { balance: bal } = await fetchBalance(tok)
      setBalance(bal)
      setBalanceWarning(null)
      return true
    } catch {
      setBalanceWarning(t('balanceRefreshFailed'))
      return false
    } finally {
      balanceRefreshRef.current = false
      setBalanceRefreshing(false)
    }
  }

  const retryRestore = async () => {
    if (
      !token ||
      restoreStatus === 'loading' ||
      restoreLockRef.current ||
      balanceRefreshRef.current
    ) return
    restoreLockRef.current = true
    setRestoreStatus('loading')
    setRestoreError(null)
    setError(null)
    try {
      const st = await minesState(token)
      if (st.status === 'active') {
        setRound(st)
        setRestoreStatus('ready')
        return
      }
      setRound(null)
      if (await refreshBal(token)) {
        setRestoreStatus('ready')
      } else {
        setRestoreStatus('error')
        setRestoreError(t('restoreBalanceFailed'))
      }
    } catch (e) {
      const raw = e instanceof Error ? e.message : ''
      if (raw.toLowerCase().includes('no active round')) {
        setRound(null)
        if (await refreshBal(token)) {
          setRestoreStatus('ready')
        } else {
          setRestoreStatus('error')
          setRestoreError(t('restoreBalanceFailed'))
        }
      } else {
        setRestoreStatus('error')
        setRestoreError(t('restoreFailed'))
      }
    } finally {
      restoreLockRef.current = false
    }
  }

  const onStart = async () => {
    if (!token || busy || restoreStatus !== 'ready' || actionLockRef.current) return
    actionLockRef.current = true
    setBusy(true)
    setError(null)
    setLastActionCell(null)
    try {
      const st = await minesStart(token, bet, mines)
      setRound(st)
      await refreshBal(token)
    } catch {
      setRestoreStatus('error')
      setRestoreError(t('actionOutcomeUnknown'))
      setError(null)
    } finally {
      actionLockRef.current = false
      setBusy(false)
    }
  }

  const onOpen = async (cell: number) => {
    if (
      !token ||
      !round ||
      round.status !== 'active' ||
      busy ||
      restoreStatus !== 'ready' ||
      actionLockRef.current
    ) return
    actionLockRef.current = true
    setBusy(true)
    setError(null)
    setLastActionCell(cell)
    try {
      const st = await minesOpen(token, cell)
      setRound(st)
      if (st.status !== 'active') await refreshBal(token)
    } catch {
      setRestoreStatus('error')
      setRestoreError(t('actionOutcomeUnknown'))
      setError(null)
    } finally {
      actionLockRef.current = false
      setBusy(false)
    }
  }

  const onCashout = async () => {
    if (!token || busy || restoreStatus !== 'ready' || actionLockRef.current) return
    actionLockRef.current = true
    setBusy(true)
    setError(null)
    try {
      const st = await minesCashout(token)
      setRound(st)
      await refreshBal(token)
    } catch {
      setRestoreStatus('error')
      setRestoreError(t('actionOutcomeUnknown'))
      setError(null)
    } finally {
      actionLockRef.current = false
      setBusy(false)
    }
  }

  const reduced = prefersReducedMotion()
  const displayBalance = useCountUp(balance)
  const shownMult = useCountUp(round?.multiplier ?? 0, 300)
  const shownNext = useCountUp(round?.next_multiplier ?? 0, 300)
  const shownCashout = useCountUp(
    round ? Math.floor(round.bet_amount * round.multiplier) : 0,
    300,
  )

  if (authLoading) {
    return (
      <div className="center" role="status" aria-live="polite">
        <div className="spinner" aria-hidden />
        <p>{t('connecting')}</p>
      </div>
    )
  }
  if (authError || !token) {
    return (
      <div className="center" role="alert">
        <p className="error">{authError ?? t('unauthorized')}</p>
        <button type="button" className="btn-lobby" onClick={() => setAuthRetry((n) => n + 1)}>
          {t('retryLogin')}
        </button>
        <button type="button" className="btn-lobby" onClick={() => goLobby(token)}>
          {t('backLobby')}
        </button>
      </div>
    )
  }

  const grid = round?.grid_size ?? 5
  const cells = round?.cells ?? Array(grid * grid).fill('hidden')
  const active = round?.status === 'active'
  const controlsLocked =
    busy ||
    balanceRefreshing ||
    restoreStatus !== 'ready' ||
    (!round && Boolean(balanceWarning))

  const drawerTabs: SheetTab[] = [
    {
      id: 'rules',
      label: t('rules'),
      content: (
        <div style={{ lineHeight: 1.6 }}>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '1rem', color: '#fff' }}>{t('rulesTitle')}</h3>
          <p style={{ color: 'var(--text-secondary)' }}>{t('rulesBody')}</p>
        </div>
      ),
    },
  ]

  // Tier 1: Parameter selection (Mine Count)
  const parameterSlot = !round ? (
    <div className="bet-dock-segmented">
      {MINE_LEVELS.map((value) => (
        <button
          key={value}
          type="button"
          className={`bet-dock-segment-btn${mines === value ? ' active' : ''}`}
          disabled={controlsLocked}
          aria-pressed={mines === value}
          onClick={() => setMines(value)}
        >
          {value} {t('mineCount')}
        </button>
      ))}
    </div>
  ) : (
    <div className="bet-dock-segmented" style={{ justifyContent: 'center', padding: '4px 0', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
      <span>{t('roundSummary', { mines: round.mine_count, opened: round.opened })}</span>
    </div>
  )

  // Tier 2: Amount input and collapsible quick chips
  const amountSlot = !round ? (
    <div className="bet-dock-tier-amount">
      <div className="bet-dock-amount-main-row">
        <div className="bet-dock-input-group">
          <label htmlFor="mines-bet">{t('bet')}</label>
          <input
            id="mines-bet"
            type="number"
            min={10}
            max={balance}
            value={bet}
            disabled={controlsLocked}
            onChange={(e) => setBet(Math.max(0, Number(e.target.value) || 0))}
          />
        </div>
        <div className="bet-dock-quick-multipliers">
          <button
            type="button"
            className="bet-dock-multiplier-btn"
            disabled={controlsLocked}
            onClick={() => setBet(Math.max(10, Math.floor(bet / 2)))}
          >
            1/2
          </button>
          <button
            type="button"
            className="bet-dock-multiplier-btn"
            disabled={controlsLocked}
            onClick={() => setBet(Math.max(10, Math.min(balance, bet * 2)))}
          >
            2X
          </button>
          <button
            type="button"
            className="bet-dock-multiplier-btn"
            disabled={controlsLocked}
            onClick={() => setBet(Math.max(10, balance))}
          >
            {t('max')}
          </button>
          <button
            type="button"
            className={`bet-dock-chip-toggle${showChips ? ' active' : ''}`}
            disabled={controlsLocked}
            onClick={() => setShowChips(!showChips)}
            title={t('quickChips')}
            aria-label={t('quickChips')}
            aria-expanded={showChips}
            aria-controls="mines-quick-chips"
          >
            {showChips ? '▲' : '⚡'}
          </button>
        </div>
      </div>
      {showChips && (
        <div id="mines-quick-chips" className="bet-dock-collapsible-chips">
          {QUICK_BETS.map((value) => (
            <button
              key={value}
              type="button"
              className="bet-dock-preset-chip"
              disabled={controlsLocked}
              onClick={() => {
                setBet(value)
                setShowChips(false)
              }}
            >
              {value}
            </button>
          ))}
        </div>
      )}
    </div>
  ) : (
    <div className="bet-dock-meta-info" style={{ width: '100%', padding: '2px 6px' }}>
      <span>{t('bet')}: {round.bet_amount}</span>
      <span>{t('multiplier')}: {shownMult.toFixed(2)}x</span>
    </div>
  )

  // Tier 3: Primary Action CTA
  let ctaSlot = null
  if (restoreStatus !== 'ready') {
    ctaSlot = (
      <motion.button
        type="button"
        className="bet-dock-cta-btn state-ready"
        whileTap={{ scale: 0.98 }}
        disabled={restoreStatus === 'loading' || busy || balanceRefreshing}
        onClick={() => void retryRestore()}
      >
        {restoreStatus === 'loading' ? t('restoringRound') : t('retryRestore')}
      </motion.button>
    )
  } else if (!round) {
    ctaSlot = (
      <motion.button
        type="button"
        className="bet-dock-cta-btn state-ready"
        whileTap={{ scale: 0.98 }}
        disabled={
          balanceRefreshing ||
          (!balanceWarning && (busy || bet < 10 || bet > balance))
        }
        onClick={() => {
          if (balanceWarning) void refreshBal(token)
          else void onStart()
        }}
      >
        {balanceRefreshing
          ? t('refreshingBalance')
          : balanceWarning
            ? t('retryBalance')
            : `${t('start')} (${bet})`}
      </motion.button>
    )
  } else if (active) {
    ctaSlot = (
      <motion.button
        type="button"
        className={`bet-dock-cta-btn state-cashout${round.opened >= 1 ? ' pulse' : ''}`}
        whileTap={{ scale: 0.98 }}
        disabled={busy || restoreStatus !== 'ready' || round.opened < 1}
        onClick={() => void onCashout()}
      >
        {t('cashout', { amount: Math.round(shownCashout) })}
      </motion.button>
    )
  } else {
    ctaSlot = (
      <motion.button
        type="button"
        className="bet-dock-cta-btn state-ready"
        whileTap={{ scale: 0.98 }}
        disabled={busy || balanceRefreshing}
        onClick={() => {
          if (balanceWarning) {
            void refreshBal(token)
          } else {
            setRound(null)
            setError(null)
            setLastActionCell(null)
          }
        }}
      >
        {balanceRefreshing
          ? t('refreshingBalance')
          : balanceWarning
            ? t('retryBalance')
            : t('again')}
      </motion.button>
    )
  }

  const metaSlot = (
    <>
      <span>{t('balance', { amount: Math.round(balance) })}</span>
      {round?.status === 'cashed' && (
        <span style={{ color: 'var(--color-win)' }}>
          {t('cashedHint', { mult: round.multiplier.toFixed(2), payout: round.payout })}
        </span>
      )}
      {round?.status === 'bombed' && (
        <span style={{ color: 'var(--color-danger)' }}>{t('bombed')}</span>
      )}
      {balanceWarning && (
        <span role="status" aria-live="polite" style={{ color: 'var(--color-warning, #f7c66b)' }}>
          {balanceWarning}
        </span>
      )}
    </>
  )

  return (
    <div
      className="app app-single-viewport"
      data-game="mines"
      style={{ ['--mines-hidden-art' as string]: `url("${hiddenCellArt}")` }}
    >
      <GameHeader
        title={t('title')}
        brandSrc={brandMark}
        lobbyLabel={t('backLobby')}
        onLobby={() => goLobby(token)}
        balance={displayBalance}
        balanceLabel={t('balance', { amount: Math.round(displayBalance) })}
        onOpenRules={() => setSheetOpen(true)}
      />

      <div className="game-stage-viewport">
        <section
          className={`mines-arena${active ? ' is-live' : ''}`}
          style={{
            width: '100%',
            height: '100%',
            minHeight: 0,
            padding: '6px 8px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            alignItems: 'center',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <img className="game-world-backdrop" src={minesWorld} alt="" aria-hidden />

          <div className="stat-strip" style={{ width: '100%', maxWidth: 360, gap: 4, margin: '0 0 4px', padding: '4px 6px' }}>
            <div className="stat" style={{ padding: '4px 6px' }}>
              <span className="stat-label">{t('multiplier')}</span>
              <span className="stat-val" style={{ fontSize: '0.95rem' }}>{round ? shownMult.toFixed(2) : '1.00'}x</span>
            </div>
            <div className="stat" style={{ padding: '4px 6px' }}>
              <span className="stat-label">{t('nextCell')}</span>
              <span className="stat-val muted" style={{ fontSize: '0.95rem' }}>{round ? shownNext.toFixed(2) : '—'}{round ? 'x' : ''}</span>
            </div>
            <div className="stat" style={{ padding: '4px 6px' }}>
              <span className="stat-label">{t('opened')}</span>
              <span className="stat-val" style={{ fontSize: '0.95rem' }}>
                <motion.span
                  key={round?.opened ?? 0}
                  style={{ display: 'inline-block' }}
                  initial={reduced ? false : { scale: 1.25 }}
                  animate={{ scale: 1 }}
                  transition={SPRING_POP}
                >
                  {round?.opened ?? 0}
                </motion.span>
                <span className="stat-unit">/{grid * grid - (round?.mine_count ?? mines)}</span>
              </span>
            </div>
          </div>

          <div
            className={`mines-board${!round ? ' is-preview' : ''}${active ? ' live' : ''}${round?.status === 'bombed' ? ' is-bombed' : ''}`}
            style={{
              ['--grid' as string]: grid,
              width: 'min(100%, 340px)',
              maxHeight: 'calc(100% - 56px)',
              aspectRatio: '1 / 1',
              padding: 6,
              gap: 4,
              gridTemplateColumns: 'repeat(5, 1fr)',
              margin: 'auto',
            }}
          >
            {cells.map((c, i) => (
              <motion.button
                key={i}
                type="button"
                className={`mine-cell ${c}${lastActionCell === i && c !== 'hidden' ? ' is-impact' : ''}`}
                style={{ minHeight: 0 }}
                disabled={!active || c !== 'hidden' || busy || restoreStatus !== 'ready'}
                initial={reduced ? false : { opacity: 0, scale: 0.85 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: Math.min(i * 0.008, 0.2), ...SPRING_SOFT }}
                whileTap={reduced || !active || c !== 'hidden' ? undefined : { y: 3, scale: 0.94 }}
                onClick={() => void onOpen(i)}
                aria-label={
                  c === 'hidden'
                    ? t('cellHidden', { n: i + 1 })
                    : c === 'mine'
                      ? t('cellMine', { n: i + 1 })
                      : t('cellSafe', { n: i + 1 })
                }
              >
                <span className="mine-mark" aria-hidden>
                  {c === 'safe' && (
                    <motion.span
                      className="mine-mark-inner"
                      initial={reduced ? false : { opacity: 0, scale: 0.58, y: 8, rotateZ: -6 }}
                      animate={{ opacity: 1, scale: 1, y: 0, rotateZ: 0 }}
                      transition={reduced ? { duration: 0 } : SPRING_SETTLE}
                    >
                      <img className="mine-asset" src={safeCrystal} alt="" />
                    </motion.span>
                  )}
                  {c === 'mine' && (
                    <motion.span
                      className="mine-mark-inner"
                      initial={
                        reduced
                          ? false
                          : lastActionCell === i
                            ? { opacity: 0, scale: 0.42, rotateZ: -12 }
                            : { opacity: 0, scale: 0.82 }
                      }
                      animate={{ opacity: 1, scale: 1, rotateZ: 0 }}
                      transition={
                        reduced
                          ? { duration: 0 }
                          : lastActionCell === i
                            ? SPRING_POP
                            : { delay: Math.min(i * 0.018, 0.28), duration: 0.32, ease: EASE_OUT }
                      }
                    >
                      <img className="mine-asset mine-asset--hazard" src={hazardCore} alt="" />
                    </motion.span>
                  )}
                </span>
              </motion.button>
            ))}
          </div>
        </section>
      </div>

      <BetDock
        className="mines-bet-dock"
        parameterSlot={parameterSlot}
        amountSlot={amountSlot}
        ctaSlot={ctaSlot}
        metaSlot={metaSlot}
        error={error ?? restoreError}
      />

      <BottomSheetDrawer
        isOpen={sheetOpen}
        onClose={() => setSheetOpen(false)}
        tabs={drawerTabs}
        closeLabel={t('close')}
      />
    </div>
  )
}
